import { useEffect, useState } from "react";
import { AlertTriangle, Wind, Navigation, Radar, Compass } from "lucide-react";
import { riskOf, countdown } from "@/lib/geo";

const confColor = (c) =>
  c >= 70 ? "#00ff87" : c >= 45 ? "#FF9F1C" : "#64748b";

const LOW_CONF = 45;
function dayRange(lo, hi) {
  if (lo == null || hi == null) return "≈ 1–3 days";
  if (hi <= 24) return "within 1 day";
  const d1 = Math.max(1, Math.round(lo / 24));
  const d2 = Math.max(d1, Math.round(hi / 24));
  return d1 === d2 ? `≈ ${d2} day${d2 > 1 ? "s" : ""}` : `≈ ${d1}–${d2} days`;
}

function Card({ isl, selected, onSelect }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const rc = riskOf(isl.risk);
  const hasEta = !!isl.eta_utc;
  const cd = hasEta ? countdown(isl.eta_utc) : { text: "> 72 H", over: false };
  const isHigh = isl.risk === "HIGH";
  const lowConf = (isl.confidence ?? 100) < LOW_CONF;
  const showRange = hasEta && lowConf && !cd.over;

  return (
    <button
      onClick={() => onSelect(isl.id)}
      data-testid={`island-risk-card-${isl.id}`}
      aria-pressed={selected}
      aria-label={`${isl.name}, ${isl.risk} risk. View details`}
      className={`glass w-full rounded-xl p-3.5 text-left transition-colors duration-300 hover:border-cyan-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
        selected ? "border-cyan-400/60 ring-1 ring-cyan-400/30" : ""
      } ${isHigh ? "alert-glow " + rc.ring : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-base font-bold text-slate-100 leading-tight">
            {isl.name}
          </div>
          <div className="eyebrow text-slate-400 mt-0.5">{isl.country}</div>
        </div>
        <span
          className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-mono-tel font-bold tracking-wider ${rc.chip}`}
        >
          {isHigh && <AlertTriangle className="mr-1 inline h-3 w-3" />}
          {rc.label}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="eyebrow text-slate-400">Expected arrival</div>
          {showRange ? (
            <>
              <div
                className="font-mono-tel text-lg font-bold tracking-wide text-slate-200"
                data-testid={`countdown-${isl.id}`}
              >
                {dayRange(isl.eta_lo, isl.eta_hi)}
              </div>
              <div className="mt-0.5 inline-flex items-center rounded border border-slate-500/40 bg-slate-500/10 px-1.5 py-0.5 font-mono-tel text-[9px] font-bold tracking-wider text-slate-400"
                data-testid={`low-confidence-${isl.id}`}>
                LOW CONFIDENCE
              </div>
            </>
          ) : (
            <>
              <div
                className={`font-mono-tel text-2xl font-bold tracking-wider ${
                  !hasEta ? "text-slate-400" : cd.over ? "text-red-400" : rc.text
                }`}
                data-testid={`countdown-${isl.id}`}
              >
                {cd.text}
              </div>
              {hasEta && isl.eta_lo != null && isl.eta_hi != null ? (
                <div className="font-mono-tel text-[10px] text-slate-400">
                  arrivals +{Math.round(isl.eta_lo)}–{Math.round(isl.eta_hi)}h
                </div>
              ) : !hasEta ? (
                <div className="font-mono-tel text-[10px] text-slate-400">
                  no landfall in 72h forecast
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className="text-right">
          <div className="eyebrow text-slate-400">Landfall ≤48h</div>
          <div className="font-mono-tel text-2xl font-bold" style={{ color: rc.hex }}
            data-testid={`prob48-${isl.id}`}>
            {isl.p_landfall_48 != null ? `${Math.round(isl.p_landfall_48 * 100)}%` : "—"}
          </div>
          <div className="font-mono-tel text-[10px] text-slate-400">{isl.distance_km} km away</div>
        </div>
      </div>

      {/* biomass density bar */}
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="eyebrow text-slate-400">Biomass density</span>
          <span className="font-mono-tel text-xs text-biomass">{isl.biomass_tkm2} t/km²</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, (isl.biomass_tkm2 / 40) * 100)}%`,
              background: "linear-gradient(90deg,#0ea5a0,#00ff87)",
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/25 bg-amber-500/5 px-2 py-1 text-amber-200/90">
          <Compass className="h-3.5 w-3.5 text-amberx" />
          <span className="font-mono-tel uppercase tracking-wide">
            {isl.impact_coast === "Offshore" ? "Offshore" : `${isl.impact_coast} coast`}
          </span>
        </span>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <Wind className="h-3.5 w-3.5 text-cyan-400/70" />
            <span className="font-mono-tel">{isl.wind_speed_ms} m/s</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Navigation
              className="h-3.5 w-3.5 text-cyan-400/70"
              style={{ transform: `rotate(${isl.wind_dir_from + 180}deg)` }}
            />
            <span className="font-mono-tel">{isl.wind_dir_from}°</span>
          </span>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px]">
          <span className="eyebrow text-slate-400">Confidence</span>
          <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-white/5">
            <span
              className="block h-full rounded-full"
              style={{ width: `${isl.confidence ?? 0}%`, background: confColor(isl.confidence) }}
            />
          </span>
          <span className="font-mono-tel text-slate-300" data-testid={`confidence-${isl.id}`}>
            {isl.confidence ?? "—"}%
          </span>
        </span>
        {isl.diverging && (
          <span className="rounded bg-slate-500/20 px-1.5 py-0.5 font-mono-tel text-[10px] text-slate-300">
            DRIFTING AWAY
          </span>
        )}
      </div>
    </button>
  );
}

export default function RiskTicker({ islands = [], selectedIsland, onSelect }) {
  const highCount = islands.filter((i) => i.risk === "HIGH").length;
  return (
    <div className="flex h-full flex-col" data-testid="risk-ticker">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-amberx" />
          <h2 className="font-display text-sm font-bold tracking-wide text-slate-100">
            Beach impact risk
          </h2>
        </div>
        {highCount > 0 && (
          <span className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 font-mono-tel text-[10px] font-bold text-amber-300">
            {highCount} HIGH
          </span>
        )}
      </div>
      <div className="thin-scroll flex-1 space-y-3 overflow-y-auto pr-1">
        {islands.map((isl, i) => (
          <div key={isl.id} className="rise" style={{ animationDelay: `${i * 45}ms` }}>
            <Card isl={isl} selected={selectedIsland === isl.id} onSelect={onSelect} />
          </div>
        ))}
      </div>
    </div>
  );
}
